import { useState } from 'react';
import { Card, Button, Form, Row, Col, Alert } from 'react-bootstrap';
import { api } from '../services/api';
import { toast } from 'react-toastify';

interface TicketRatingProps {
  ticketId: string;
  currentRating?: number;
  currentComment?: string;
  onRatingSubmitted: () => void;
}

export const TicketRating = ({ ticketId, currentRating, currentComment, onRatingSubmitted }: TicketRatingProps) => {
  const [rating, setRating] = useState(currentRating || 0);
  const [comment, setComment] = useState(currentComment || '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      await api.patch(`/tickets/${ticketId}/rating`, {
        rating,
        ratingComment: comment.trim() || undefined
      });

      toast.success('Thank you for your feedback!');
      onRatingSubmitted();
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to submit rating');
    } finally {
      setSubmitting(false);
    }
  };

  const StarRating = ({ value, onChange }: { value: number; onChange: (rating: number) => void }) => {
    return (
      <div className="d-flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            className={`btn btn-link p-0 ${star <= value ? 'text-warning' : 'text-muted'}`}
            onClick={() => onChange(star)}
            style={{ fontSize: '1.5rem', border: 'none', background: 'none' }}
          >
            <i className={`fas fa-star ${star <= value ? 'fas' : 'far'}`}></i>
          </button>
        ))}
      </div>
    );
  };

  const getRatingText = (rating: number) => {
    switch (rating) {
      case 1: return 'Poor';
      case 2: return 'Fair';
      case 3: return 'Good';
      case 4: return 'Very Good';
      case 5: return 'Excellent';
      default: return '';
    }
  };

  return (
    <Card className="border-warning">
      <Card.Header className="bg-warning bg-opacity-10">
        <h6 className="mb-0">
          <i className="fas fa-star me-2 text-warning"></i>
          Rate Your Experience
        </h6>
      </Card.Header>
      <Card.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        
        <Form onSubmit={handleSubmit}>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold">
                  How would you rate the support you received?
                </Form.Label>
                <div className="d-flex align-items-center gap-3">
                  <StarRating value={rating} onChange={setRating} />
                  {rating > 0 && (
                    <span className="text-muted fw-medium">
                      {getRatingText(rating)}
                    </span>
                  )}
                </div>
                <Form.Text className="text-muted">
                  Click on the stars to rate (1 = Poor, 5 = Excellent)
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">
              <i className="fas fa-comment me-2 text-info"></i>
              Additional Comments (Optional)
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Tell us about your experience with this ticket..."
              maxLength={500}
            />
            <Form.Text className="text-muted">
              {comment.length}/500 characters
            </Form.Text>
          </Form.Group>

          <div className="d-flex gap-2">
            <Button
              type="submit"
              variant="warning"
              disabled={rating === 0 || submitting}
            >
              {submitting ? (
                <>
                  <i className="fas fa-spinner fa-spin me-2"></i>
                  Submitting...
                </>
              ) : (
                <>
                  <i className="fas fa-star me-2"></i>
                  Submit Rating
                </>
              )}
            </Button>
            
            {currentRating && (
              <Button
                variant="outline-secondary"
                onClick={() => {
                  setRating(currentRating);
                  setComment(currentComment || '');
                }}
              >
                <i className="fas fa-undo me-2"></i>
                Reset
              </Button>
            )}
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};
